// src/components/Inquiry/items/InquiryItemsTable.tsx
//
// The item lines of an inquiry, in one table.
//
// There were two before: a read-only one for a saved inquiry and an editable
// pair (a picker table plus a free-text table) for one being written. They
// showed the same four things in different orders under different headings,
// and every fix had to be made twice or it was not made at all - the dropped
// quantity on re-tick, the hardcoded price and "undefinedpcs" were each one
// table knowing something the other did not.
//
// `mode` is the only difference now: edit mode adds a leading tick/bin column,
// swaps the Files column for the drawing menu, and puts an "Add other item"
// row at the bottom. Read mode, handed a GoodsInState, appends the goods-in
// count columns - the lines are where the buyer counts what arrived.
import { FunctionComponent, useContext, useState } from "react";
import {
  Button,
  Checkbox,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import AppStateContext from "contexts/AppStateContext";
import { useToast } from "contexts/ToastContext";
import { apiCreateProduct } from "util/network/Products";
import { CartItemType } from "types/CartTypes";
import { InquiryTypeEnum } from "types/InquiryTypes";
import { OrderItemType } from "types/OrderTypes";
import {
  CreateOtherProductType,
  ProductTypeEnum,
} from "types/products/ProductCommandsType";
import { ROW_FONT_MICRO, SP_1, SP_2 } from "components/Dashboard/dashboardTable";
import { SelectedItem } from "../hooks/useInquiryItems";
import OtherLineRow from "./OtherLineRow";
import ProductLineRow from "./ProductLineRow";
import {
  INQUIRY_ITEM_COLUMNS,
  SELECT_COLUMN,
  itemCellProps,
  showsQuantity,
} from "./inquiryItemColumns";
import { InquiryLine, lineFromOrderItem } from "./inquiryLine";
import {
  COUNT_CELL_SX,
  GOODS_IN_COLUMNS,
  GOODS_IN_LAYOUT,
  GoodsInCells,
  GoodsInState,
  receivedLabel,
} from "./goodsIn";

/**
 * Which inquiry these lines belong to. Only the Files column reads it - what
 * a supplier is sent of the part depends on what they are being asked for
 * (ProductLineRow) - but it is common to both modes.
 */
type CommonProps = {
  inquiryType?: InquiryTypeEnum;
};

type ReadProps = CommonProps & {
  mode: "read";
  lines: InquiryLine[];
  /** From Pickup / Delivery on: the Received / Ready to pick up columns. */
  goodsIn?: GoodsInState;
};

type EditProps = CommonProps & {
  mode: "edit";
  /** What the order or quote offers, already filtered by inquiry type. */
  prodItems: (OrderItemType | CartItemType)[];
  selectedItems: SelectedItem[];
  /** Free-text lines. They are not on the order, so they are always on. */
  customItems: (OrderItemType | CartItemType)[];
  prodType: "order" | "cart";
  toggle: (item: SelectedItem) => void;
  selectAll: () => void;
  deselectAll: () => void;
  addCustomItem: (item: OrderItemType | CartItemType) => void;
  updateCustomItem: (item: OrderItemType | CartItemType) => void;
  removeCustomItem: (itemId: number) => void;
};

const HEAD_CELL_SX = {
  padding: `${SP_1} ${SP_2}`,
  fontSize: ROW_FONT_MICRO,
  textTransform: "uppercase" as const,
  letterSpacing: "0.04em",
  color: "var(--black75)",
  borderBottom: "1px solid var(--black10)",
};

const NEW_OTHER_PRODUCT: CreateOtherProductType = {
  name: "Custom item",
  price: 0,
  weight: 0,
  description: "",
  fileResources: [],
  productType: ProductTypeEnum.OTHER,
};

const InquiryItemsTable: FunctionComponent<ReadProps | EditProps> = (props) => {
  const { token } = useContext(AppStateContext);
  const { addToast } = useToast();
  const [adding, setAdding] = useState(false);

  const addOtherItem = () => {
    if (props.mode !== "edit") return;
    setAdding(true);
    apiCreateProduct(token, NEW_OTHER_PRODUCT)
      .then((product) =>
        props.addCustomItem({
          // The id is the product's: the row writes the name, description and
          // files straight back onto that product.
          id: product.id,
          product,
          quantity: 1,
          price: 0,
          description: "",
        } as unknown as OrderItemType)
      )
      .catch((err) =>
        addToast({
          type: "error",
          message: `The line could not be added: ${err}`,
          keep: true,
        })
      )
      .finally(() => setAdding(false));
  };

  const goodsIn = props.mode === "read" ? props.goodsIn : undefined;

  const allSelected =
    props.mode === "edit" &&
    props.prodItems.length > 0 &&
    props.selectedItems.length === props.prodItems.length;

  const showQty = showsQuantity(props.inquiryType);

  const table = (
    <Table size="small" sx={{ tableLayout: "fixed" }}>
      {/* The row variants only read as one table if something states the grid
          they share - see inquiryItemColumns. */}
      <TableHead>
        <TableRow>
          {props.mode === "edit" && (
            <TableCell
              {...itemCellProps(SELECT_COLUMN, HEAD_CELL_SX)}
              padding="checkbox"
            >
              <Checkbox
                checked={allSelected}
                indeterminate={
                  props.selectedItems.length > 0 &&
                  props.selectedItems.length < props.prodItems.length
                }
                onChange={(e) =>
                  e.target.checked ? props.selectAll() : props.deselectAll()
                }
              />
            </TableCell>
          )}
          {/* A fixed layout takes its column widths from this row, so the
              goods-in re-cut only has to be applied here. */}
          {Object.entries(INQUIRY_ITEM_COLUMNS)
            .filter(([key]) => key !== "qty" || showQty)
            .map(([key, column]) => (
              <TableCell
                key={key}
                {...itemCellProps(column, {
                  ...HEAD_CELL_SX,
                  ...(goodsIn && {
                    width: GOODS_IN_LAYOUT[key as keyof typeof GOODS_IN_LAYOUT],
                  }),
                })}
              >
                {column.label}
              </TableCell>
            ))}
          {goodsIn?.pickup && (
            <TableCell
              {...itemCellProps(GOODS_IN_COLUMNS.ready, {
                ...HEAD_CELL_SX,
                ...COUNT_CELL_SX,
              })}
            >
              {GOODS_IN_COLUMNS.ready.label}
            </TableCell>
          )}
          {goodsIn && (
            <TableCell
              {...itemCellProps(GOODS_IN_COLUMNS.received, {
                ...HEAD_CELL_SX,
                ...COUNT_CELL_SX,
              })}
            >
              {receivedLabel(goodsIn.pickup)}
            </TableCell>
          )}
          {goodsIn && (
            <TableCell
              {...itemCellProps(GOODS_IN_COLUMNS.readyToUse, {
                ...HEAD_CELL_SX,
                ...COUNT_CELL_SX,
              })}
            >
              {GOODS_IN_COLUMNS.readyToUse.label}
            </TableCell>
          )}
        </TableRow>
      </TableHead>
      <TableBody>
        {props.mode === "read"
          ? props.lines.map((line) => {
              const trailing = goodsIn && (
                <GoodsInCells goodsIn={goodsIn} line={line} />
              );
              // A line with no product at all is a Requisition converted
              // before EE-253; OtherLineRow reads its spec from `comment`.
              return !line.product ||
                line.product.productType === ProductTypeEnum.OTHER ? (
                <OtherLineRow
                  key={line.key}
                  mode="read"
                  line={line}
                  inquiryType={props.inquiryType}
                  trailing={trailing}
                />
              ) : (
                <ProductLineRow
                  key={line.key}
                  mode="read"
                  line={line}
                  inquiryType={props.inquiryType}
                  trailing={trailing}
                />
              );
            })
          : [
              ...props.customItems.map((item) => (
                <OtherLineRow
                  key={`custom-${item.id}`}
                  mode="edit"
                  item={item}
                  inquiryType={props.inquiryType}
                  onUpdate={props.updateCustomItem}
                  onDelete={(deleted) => props.removeCustomItem(deleted.id)}
                />
              )),
              // Every line the order offers is picked the same way, including
              // an OTHER product bought through it - ProductLineRow shows what
              // such a line has and EMPTY where it has nothing, rather than
              // dropping out of the grid.
              ...props.prodItems.map((item) => {
                const line = lineFromOrderItem(item, props.prodType);
                const selected = props.selectedItems.some(
                  (s) => s.productId === line.productId
                );
                return (
                  <ProductLineRow
                    key={item.id}
                    mode="edit"
                    line={line}
                    inquiryType={props.inquiryType}
                    selected={selected}
                    onToggle={() =>
                      props.toggle({
                        productId: line.productId,
                        // Without this the line goes to the API with no
                        // quantity and the backend defaults it to 1, quietly
                        // replacing what was ordered.
                        quantity: line.quantity ?? 1,
                        orderItemId: line.orderItemId,
                        quoteItemId: line.quoteItemId,
                      })
                    }
                  />
                );
              }),
            ]}
        {props.mode === "edit" && (
          <TableRow>
            <TableCell align="center" colSpan={showQty ? 5 : 4}>
              <Button onClick={addOtherItem} disabled={adding}>
                Add other item
              </Button>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );

  if (props.mode === "read") return table;

  return (
    <Paper>
      {adding && <LinearProgress />}
      {table}
    </Paper>
  );
};

export default InquiryItemsTable;
